import { Styled } from "./styles";
import { withErrorBoundary } from "@/components/hoc";
import { capitalize, setEntities } from "@/utils/helpers";
import { log } from "@/utils/logger";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/context/user";
import { IDCardWhite, PersonWhite } from "@dippi/icons";
import { auth } from "@/config/firebase";
import { useEffect, useState } from "react";
import { CustomClaims } from "@/types/utils";
import storage from "@/utils/storage";
import api from "@/config/api";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import useQueryOrganizations from "@/hooks/queries/useQueryOrganizations";

const Page: React.FC = () => {
    const navigate = useNavigate();
    const login = useUser(state => state.login);

    const { data: organizations } = useQueryOrganizations();
    const setGlobalLoading = useUser(state => state.setGlobalLoading);
    const [selectedOrg, setSelectedOrg] = useState("");

    const onSelect = async (oid: string) => {
        try {
            setGlobalLoading(true);
            setSelectedOrg(oid), storage.setItem("oid", oid);

            await api.post("/users/set-claims", { data: { oid } });
            await setEntities(oid);

            login();
            navigate(`/organization/${oid}/brands`);
        } catch (e) {
            log.error(e, "onSelect");
        } finally {
            setGlobalLoading(false);
        }
    };

    const user = auth.currentUser;

    const getClaims = async () => {
        try {
            setGlobalLoading(true);
            const result = await user?.getIdTokenResult();
            const claims = result?.claims as CustomClaims;

            if (claims.organization && claims.organization.oid !== selectedOrg) {
                setSelectedOrg(claims.organization.oid);
            }
        } catch (e) {
            log.error(e, "getClaims");
        } finally {
            setGlobalLoading(false);
        }
    };

    useEffect(() => {
        if (!user) return;
        getClaims();
    }, [user]);

    if (!organizations) {
        return <Styled.Container />;
    }

    return (
        <Styled.Container>
            <Typography variant="h3">Choose organization</Typography>
            <Styled.List>
                {Object.entries(organizations).map((i, index) => {
                    const oid = i[0];
                    const { role, organization } = i[1];
                    const { title, companyId } = organization;

                    return (
                        <Styled.ListItem
                            data-testid={`organization-${index}`}
                            key={oid}
                            onClick={onSelect.bind(this, oid)}
                            selected={oid === selectedOrg}
                            id={oid}
                        >
                            <Box>
                                <Box display="flex" alignItems="center" gap="8px">
                                    <Typography fontWeight={700}>{title}</Typography>
                                    {oid === selectedOrg && (
                                        <RadioButtonCheckedIcon
                                            sx={{ width: "18px" }}
                                            data-testid={`checked-organization-${index}`}
                                        />
                                    )}
                                </Box>
                                <Box display="flex" alignItems="center" marginTop="10px">
                                    <IDCardWhite style={{ marginRight: 5 }} />
                                    <Styled.CompanyId variant="caption">
                                        {companyId}
                                    </Styled.CompanyId>
                                </Box>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <PersonWhite style={{ marginRight: 5 }} />
                                <Typography>{capitalize(role.replace("_", " "))}</Typography>
                            </Box>
                        </Styled.ListItem>
                    );
                })}
            </Styled.List>
        </Styled.Container>
    );
};

export default withErrorBoundary({ component: "Organizations" })(Page);
